import { useContext } from '@fipnooone/react-modal';
import { useEffect, useState } from 'react';

import { useForm } from '@.hooks';
import { ConsoleDev } from '@/Lib/ConsoleDev';
import { SaveStationDeviceLicenseKeyFail, SaveStationDeviceLicenseKeySuccess } from '@/Services/IRomaStationWS';

import { stationDetailStoreSelectors } from '../../../../../model/StationDetailStore';
import { EditLicenseKeySocketFormState } from './EditLicenseKeySocketFormState';
import { EditLicenseKeySocketValidateOptions } from './EditLicenseKeySocketValidateOptions';

type UseDeviceCardParams = {
    deviceId: number;
    licenseKeyInitial: string;
    updateDeviceLicenseKey: (deviceId: number, licenseKey: string) => void;
};

export const useEditLicenseKeySocket = ({ updateDeviceLicenseKey, licenseKeyInitial, deviceId }: UseDeviceCardParams) => {
    const [isLoading, setLoading] = useState<boolean>(false);

    const [error, setError] = useState<string | null>(null);

    const [field, form] = useForm<EditLicenseKeySocketFormState>({
        licenseKey: licenseKeyInitial || '',
    });

    const { close } = useContext();

    const saveNewLicenseKeyForDevice = stationDetailStoreSelectors.use.saveNewLicenseKeyForDevice();
    const iRomaStationWS = stationDetailStoreSelectors.use.iRomaStationWS();

    const requestSaveLicenseKey = () => {
        setError(null);
        setLoading(false);

        form.ifValid(async (data) => {
            setLoading(true);

            try {
                const response = await saveNewLicenseKeyForDevice(deviceId, data.licenseKey);

                if (response instanceof SaveStationDeviceLicenseKeySuccess) {
                    updateDeviceLicenseKey(deviceId, response.licenseKey);

                    close();
                } else if (response instanceof SaveStationDeviceLicenseKeyFail) {
                    setError(response.errorMessage);
                }
            } catch (error) {
                ConsoleDev.error('Failed save new license key: ', error);

                if (error instanceof SaveStationDeviceLicenseKeyFail) {
                    setError(error.errorMessage);
                } else {
                    setError('Failed save new license key');
                }
            } finally {
                setLoading(false);
            }
        }, EditLicenseKeySocketValidateOptions);
    };

    useEffect(() => {
        if (iRomaStationWS) {
            iRomaStationWS.onStationDisconnected(() => {
                close();
            });
        }
    }, [iRomaStationWS]);

    return {
        isLoading,
        error,
        field,
        close,
        requestSaveLicenseKey,
    };
};
